import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {AsyncHandler, Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ExtendedError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useToggle} from '../../../../../srcCommon/helpers/hookHelpers';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {buildAvailableSlotsSummary} from '../../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {
  isSchedulingLinkAvailable,
  isSchedulingLinkOutdated,
  schedulingLinkAdvanceNoticeOptions,
  schedulingLinkMeetingBufferOptions,
  SchedulingLinkPropertiesEnum,
} from '../../../../helpers/models/schedulingLinkHelpers';
import {DraftSchedulingLinkModel, SchedulingLinkModel} from '../../../../models/api/schedulingLinkModel';
import {VisualParticipantModel} from '../../../../models/api/visualParticipantModel';
import {HttpNetworkError, HttpTimeoutError} from '../../../../services/http/httpError';
import {CalendarsPanelId} from '../../../../store/panels/panelStateBase';
import {Dialog} from '../../../common/dialogs/dialog';
import {DialogContent} from '../../../common/dialogs/dialogContent';
import {DialogHeader} from '../../../common/dialogs/dialogHeader';
import {CalendarEventParticipantsList} from '../../participants/calendarEventParticipantsList';
import {outdatedSchedulingLinkCopy, schedulingLinkPropertyTooltips} from '../schedulingLinkViewHelpers';
import {DurationDropdownBox} from '../settings/durationDropdownBox';
import {SchedulingLinkDetailRow} from '../settings/schedulingLinkDetailRow';
import {SchedulingLinkSettingsCalendarDialogContainer} from '../settings/schedulingLinkSettingsCalendarDialogContainer';

/*
 * Props.
 */

interface InstantReschedulingLinkDialogProps {
  panelId: CalendarsPanelId;
  schedulingLinkId: number;
  draftLink: DraftSchedulingLinkModel;
  meetingBufferMinutes?: number;
  advanceNoticeMinutes?: number;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  weekStartDay: any;
  onUpdateAdvanceNoticeMinutes: HandlerOf<number>;
  onUpdateMeetingBufferMinutes: HandlerOf<number>;
  onDialogClose: Handler;
  onUpdateSchedulingLink: HandlerOf<Partial<DraftSchedulingLinkModel>>;
  onSaveClick: AsyncHandler<SchedulingLinkModel>;
}

/*
 * Style.
 */

const StyledAvailabilitySummaryDiv = styled.div`
  padding: 7px 0px 4px;

  display: grid;
  grid-template-columns: auto 1fr;
  grid-row-gap: 5px;
  align-items: center;
  grid-template-areas:
    'icon summary'
    'error error';
`;

const StyledAvailableTimesDiv = styled.div`
  margin-top: 20px;
`;

const StyledInstantReschedulingSpaceRowDiv = styled.div`
  padding-top: 20px;
`;

const StyledInstantReschedulingParticipantsRowDiv = styled.div`
  margin-left: 4px;
  padding-top: 10px;
`;

const StyledDropdownWrapperDiv = styled.div`
  width: 280px;
  > div {
    padding: 0px;
  }
`;

const StyledErrorDiv = styled.div`
  grid-area: error;
  color: ${(p) => p.theme.palette.red.shade40};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
`;

const StyledTextSummaryDiv = styled.div`
  grid-area: summary;
`;

const StyledIconWrapper = styled.div`
  grid-area: icon;
  height: 14px;
  width: 14px;
  margin-right: 5px;
`;

const StyledInstantReschedulingDiv = styled(DialogContent)`
  flex: 1;
  overflow: auto;
  padding: 0px 5px;
`;

const StyledAddMeetingTimesButtonWrapperDiv = styled.div`
  display: flex;
`;

const StyledFooterDiv = styled.div`
  grid-area: footer;
`;

const StyledSeparatorDiv = styled.div`
  height: 1px;
  background-color: ${(p) => p.theme.greys.shade30};
`;

const StyledDialogButtonGroupWrapperDiv = styled.div`
  height: 68px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-right: 20px;
`;

/*
 * Component.
 */

export const InstantReschedulingLinkDialog: FC<InstantReschedulingLinkDialogProps> = (props) => {
  const [isShowingCalendar, toggleIsShowingCalendar] = useToggle();

  // Input validation
  const [hasNoSelectedTimes, setHasNoSelectedTimes] = useState(false);

  // Dialog footer button handlers.
  const onCancelClick = () => props.onDialogClose();
  const onSaveClick = async () => {
    try {
      await props.onSaveClick();
      props.onDialogClose();
    } catch (error) {
      if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
        return;
      }

      // TODO: graceful handling. set a state variable with the error type
      // and render a message to the user.
      reportToBugsnag(new ExtendedError('Failed to update instant reschedule link.', error));
    }
  };

  return (
    <Dialog width={400} height={558}>
      <DialogHeader
        onRequestClose={props.onDialogClose}
        title={<Trans id="instant-reschedule-link-settings-title">Edit availability</Trans>}
      />
      <StyledInstantReschedulingDiv>
        <Trans id="instant-reschedule-title-dialog">
          These settings give you control over when your guests can reschedule meetings. The availability you
          choose will apply to all events.
        </Trans>
        {renderAvailableTimesRow(props, toggleIsShowingCalendar, setHasNoSelectedTimes, hasNoSelectedTimes)}
        <StyledInstantReschedulingSpaceRowDiv>
          <Trans id="instant-reschedule-participants-title-dialog">
            Front automatically checks for scheduling conflicts on the following calendars:
          </Trans>
        </StyledInstantReschedulingSpaceRowDiv>
        {renderParticipants(props)}
        {renderBufferRow(props)}
        {renderAdvanceNoticeRow(props)}
      </StyledInstantReschedulingDiv>

      {isShowingCalendar && (
        <SchedulingLinkSettingsCalendarDialogContainer
          onRequestClose={toggleIsShowingCalendar}
          hasOnlyRecurringDialog
          panelId={props.panelId}
        />
      )}
      {renderFooter(onCancelClick, onSaveClick)}
    </Dialog>
  );
};

function renderParticipants(props: InstantReschedulingLinkDialogProps) {
  return (
    <StyledInstantReschedulingParticipantsRowDiv>
      <CalendarEventParticipantsList
        panelId={props.panelId}
        visualParticipants={props.visualParticipants}
        shouldShowAllParticipants
        isInEventEditor
      />
    </StyledInstantReschedulingParticipantsRowDiv>
  );
}

function renderAdvanceNoticeRow(props: InstantReschedulingLinkDialogProps) {
  return (
    <StyledInstantReschedulingSpaceRowDiv>
      <SchedulingLinkDetailRow
        label={<Trans id="instant-reschedulingLink-settings-advancenotice">Advance notice</Trans>}
        renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.ADVANCE_NOTICE]}
      >
        <StyledDropdownWrapperDiv>
          <DurationDropdownBox
            duration={props.advanceNoticeMinutes || 0}
            onDurationSelect={props.onUpdateAdvanceNoticeMinutes}
            options={schedulingLinkAdvanceNoticeOptions}
            renderNullValue={() => (
              <Trans id="instant-reschedulingLink-settings-advancenotice-none">No advance notice</Trans>
            )}
          />
        </StyledDropdownWrapperDiv>
      </SchedulingLinkDetailRow>
    </StyledInstantReschedulingSpaceRowDiv>
  );
}

function renderBufferRow(props: InstantReschedulingLinkDialogProps) {
  return (
    <StyledInstantReschedulingSpaceRowDiv>
      <SchedulingLinkDetailRow
        label={<Trans id="instant-reschedulingLink-settings-buffer">Meeting buffer</Trans>}
        renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.MEETING_BUFFER]}
      >
        <StyledDropdownWrapperDiv>
          <DurationDropdownBox
            duration={props.meetingBufferMinutes || 0}
            onDurationSelect={props.onUpdateMeetingBufferMinutes}
            options={schedulingLinkMeetingBufferOptions}
            renderNullValue={() => (
              <Trans id="instant-reschedulingLink-meetingbuffer-none">No meeting buffer</Trans>
            )}
          />
        </StyledDropdownWrapperDiv>
      </SchedulingLinkDetailRow>
    </StyledInstantReschedulingSpaceRowDiv>
  );
}

function renderAvailableTimesRow(
  props: InstantReschedulingLinkDialogProps,
  toggleIsShowingCalendar: Handler,
  setHasNoSelectedTimes: HandlerOf<boolean>,
  hasNoSelectedTimes: boolean,
) {
  const isLinkAvailable = isSchedulingLinkAvailable(props.draftLink);
  const onClick = () => {
    setHasNoSelectedTimes(false);
    toggleIsShowingCalendar();
  };

  return (
    <StyledAvailableTimesDiv>
      <SchedulingLinkDetailRow
        label={<Trans id="instant-reschedule-settings-availabletimes">Available times</Trans>}
      >
        {isLinkAvailable && renderAvailabilitySummary(props)}
        <StyledAddMeetingTimesButtonWrapperDiv>
          <Button size={VisualSizesEnum.MEDIUM} variant={ButtonVariantsEnum.LINK} onClick={onClick}>
            {isLinkAvailable ? (
              <Trans id="instant-reschedule-settings-availability-edittimes">Edit times</Trans>
            ) : (
              <Trans id="instant-reschedule-settings-availability-addtimes">
                + Add available meeting times
              </Trans>
            )}
          </Button>
        </StyledAddMeetingTimesButtonWrapperDiv>
      </SchedulingLinkDetailRow>
    </StyledAvailableTimesDiv>
  );
}

function renderAvailabilitySummary(props: InstantReschedulingLinkDialogProps) {
  const isOutdated = isSchedulingLinkOutdated(props.draftLink);
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledAvailabilitySummaryDiv>
          {!isOutdated && (
            <StyledIconWrapper>
              <Icon name="checkmarkYesActivity" color={theme.palette.green.shade40} />
            </StyledIconWrapper>
          )}
          <StyledTextSummaryDiv>
            {buildAvailableSlotsSummary(props.draftLink, props.weekStartDay)}
          </StyledTextSummaryDiv>
          {isOutdated && <StyledErrorDiv>{outdatedSchedulingLinkCopy}</StyledErrorDiv>}
        </StyledAvailabilitySummaryDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderFooter(onCancelClick: Handler, onSaveClick: Handler) {
  return (
    <StyledFooterDiv>
      <StyledSeparatorDiv />
      <StyledDialogButtonGroupWrapperDiv>
        <ButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={onCancelClick}>
            <Trans id="instant-reschedule-settings-cancel">Cancel</Trans>
          </Button>
          <Button onClick={onSaveClick} variant={ButtonVariantsEnum.PRIMARY}>
            <Trans id="instant-reschedule-settings-save">Save</Trans>
          </Button>
        </ButtonGroup>
      </StyledDialogButtonGroupWrapperDiv>
    </StyledFooterDiv>
  );
}
